import { useRef } from 'react';

import { ActionInputHandle, Privacy } from '@bonusly/common-action-input';
import { useBonusBoxAnalytics, useSharedConfig } from '@bonusly/common-give-box';

import { Award, BonusPrivacy } from '../../../../../../../types/bonus';
import { parseErrorMessage } from '../../../../../utils/error';
import { useAwardBudget } from './useAwardBudget';
import { useGiveAward } from './useGiveAward';

interface UseSharedAwardConfig {
	award: Award;
	hideGiver: boolean;
	isTemplatedAward: boolean;
	onSuccess?: VoidFunction;
}

export const useSharedAwardConfig = ({ award, hideGiver, isTemplatedAward, onSuccess }: UseSharedAwardConfig) => {
	const inputRef = useRef<ActionInputHandle>(null);
	const analytics = useBonusBoxAnalytics();
	const handlers = useSharedConfig({
		includeSelf: true,
		includeGroups: true,
	});
	const budget = useAwardBudget(award);

	const { isSubmitting, onSubmit } = useGiveAward({
		award,
		hideGiver,
		isTemplatedAward,
		onError: (err: unknown) => {
			const message = parseErrorMessage(err);
			inputRef.current?.error(message);
		},
		onSuccess: () => {
			onSuccess?.();
			inputRef.current?.reset();
		},
	});

	const defaultPrivacy = convertBonusPrivacyToPrivacy(award.bonus_privacy);

	return {
		budget,
		initialPrivacy: defaultPrivacy,
		initialPrivacyButtonEnabled: award.bonus_privacy_changeable,
		isSubmitting,
		onSubmit,
		onStartDraft: analytics.trackBonusStart,
		ref: inputRef,
		...handlers,
	};
};

const convertBonusPrivacyToPrivacy = (bonusPrivacy: BonusPrivacy) => {
	switch (bonusPrivacy) {
		case BonusPrivacy.Public:
			return Privacy.Public;
		case BonusPrivacy.Private:
			return Privacy.Private;
		case BonusPrivacy.Team:
			return Privacy.Team;
		default:
			return Privacy.Public;
	}
};
